import { useCallback, useState } from 'react';
import { CommunityApiError, requestEmailVerification } from '../runtime/api';

/**
 * The "confirm your email" surface.
 *
 * Replaces the catch-all "Access denied" screen for the ONE case where the user
 * is not locked out at all, they just have not confirmed their address yet.
 * Telling that person they lack admin privileges sends them to an operator for a
 * problem they can fix themselves in about ten seconds.
 *
 * Shared on purpose: every tenant app hits the same wall the moment the dialer
 * requires a verified address for email-based sign-in, so the copy, the resend
 * call, the rate-limit handling and the announcement behaviour live here once.
 * An app adopts it by rendering it when the signed-in user has
 * `emailVerified === false` and the access probe denied them.
 *
 * Styling follows the package contract: CSS custom properties only, no CSS
 * shipped, so the consuming app's tokens decide how it looks.
 */

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent' }
  | { kind: 'rateLimited' }
  | { kind: 'error' };

export interface VerifyEmailNoticeProps {
  /** The address the confirmation goes to. Shown so the user can spot a typo. */
  email?: string | null;
  /** Brand label for the heading. Defaults to a neutral phrasing. */
  brandName?: string;
  /** Sign-out action, offered for the wrong-account case. Hidden when absent. */
  onSignOut?: () => void;
  /**
   * Override the resend call. Defaults to the package's own client, which is
   * what every app should use. Present so tests and Storybook can inject.
   */
  onResend?: () => Promise<void>;
}

const MESSAGES: Record<Exclude<Status['kind'], 'idle' | 'sending'>, string> = {
  sent: 'Sent. Check your inbox, and your spam folder if it is not there.',
  rateLimited:
    'You have asked for a few of these already. Wait a couple of minutes, then try again.',
  error: 'Could not send the email just now. Try again in a moment.',
};

export function VerifyEmailNotice({
  email,
  brandName,
  onSignOut,
  onResend,
}: VerifyEmailNoticeProps) {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const resend = useCallback(async () => {
    setStatus({ kind: 'sending' });
    try {
      await (onResend ? onResend() : requestEmailVerification());
      setStatus({ kind: 'sent' });
    } catch (error) {
      if (error instanceof CommunityApiError && error.status === 429) {
        setStatus({ kind: 'rateLimited' });
        return;
      }
      setStatus({ kind: 'error' });
    }
  }, [onResend]);

  const heading = brandName ? `Confirm your email for ${brandName}` : 'Confirm your email';
  const feedback = status.kind === 'idle' || status.kind === 'sending' ? null : MESSAGES[status.kind];
  const isProblem = status.kind === 'error' || status.kind === 'rateLimited';

  return (
    <section style={styles.wrap} aria-labelledby="verify-email-heading">
      <h1 id="verify-email-heading" style={styles.heading}>
        {heading}
      </h1>

      <p style={styles.body}>
        Your account is set up. Confirm your email address to finish signing in.
      </p>

      {email ? (
        <p style={styles.body}>
          We sent a confirmation link to <strong>{email}</strong>. Open it, then come
          back and sign in again.
        </p>
      ) : (
        <p style={styles.body}>
          Open the confirmation link we sent you, then come back and sign in again.
        </p>
      )}

      <div style={styles.actions}>
        <button
          type="button"
          onClick={resend}
          disabled={status.kind === 'sending'}
          style={styles.button}
        >
          {status.kind === 'sending' ? 'Sending...' : 'Send it again'}
        </button>

        {onSignOut ? (
          <button type="button" onClick={onSignOut} style={styles.secondaryButton}>
            Use a different account
          </button>
        ) : null}
      </div>

      {/*
        One live region that is ALWAYS mounted and always present in the
        accessibility tree. Mounting a region at the same moment its text
        appears is unreliable across screen readers, so the node
        stays put and only its text changes. `role="status"` is polite, which is
        right here: the user pressed the button, so the update is expected.
        It renders EMPTY rather than hidden when there is nothing to say.
        `visibility: hidden` and `display: none` both drop the node out of
        the accessibility tree, which is the exact failure that keeping it
        mounted was meant to avoid. `minHeight` reserves the space.

        Every state carries its own words. Colour is a second signal on top of
        the sentence, never the only one, so this reads the same to someone who
        cannot distinguish the two shades.
      */}
      <p
        role="status"
        aria-live="polite"
        style={{
          ...styles.feedback,
          color: isProblem ? 'var(--color-error, #DC2626)' : 'var(--color-success, #059669)',
        }}
      >
        {feedback ?? ''}
      </p>
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    maxWidth: '32rem',
    margin: '0 auto',
    padding: 'var(--space-6, 24px)',
    textAlign: 'left',
  },
  heading: {
    fontSize: 'var(--text-xl, 1.25rem)',
    margin: '0 0 var(--space-4, 16px)',
    color: 'var(--color-text-primary, #111827)',
  },
  body: {
    fontSize: 'var(--text-base, 1rem)',
    lineHeight: 1.5,
    margin: '0 0 var(--space-3, 12px)',
    color: 'var(--color-text-secondary, #374151)',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--space-3, 12px)',
    margin: 'var(--space-4, 16px) 0 var(--space-2, 8px)',
  },
  button: {
    padding: 'var(--space-3, 12px) var(--space-4, 16px)',
    borderRadius: 'var(--radius-md, 6px)',
    border: 'none',
    background: 'var(--color-primary, #2563eb)',
    color: '#ffffff',
    fontSize: 'var(--text-base, 1rem)',
    fontWeight: 600,
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: 'var(--space-3, 12px) var(--space-4, 16px)',
    borderRadius: 'var(--radius-md, 6px)',
    border: '1px solid var(--color-border, #D1D5DB)',
    background: 'transparent',
    color: 'var(--color-text-primary, #111827)',
    fontSize: 'var(--text-base, 1rem)',
    cursor: 'pointer',
  },
  feedback: {
    fontSize: 'var(--text-sm, 0.875rem)',
    margin: 0,
    minHeight: '1.25rem',
  },
};
